import { AnimatePresence } from 'framer-motion';
import { FC, ReactNode, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import { CrossfadePanel, CrossfadeText } from '../../../../components/motion/index';
import { VestingTaskHooks } from '../../../../hooks/index';
import { VestingTask } from '../../../../types/vesting-tasks.types';
import { VestingGraph } from './chart/vesting-graph';
import { VestingGraphSubtitle } from './chart/vesting-graph-subtitle';
import { buildVestingGraphSubtitleParts, formatShortGrantId } from './model/vesting-graph.format';
import { VestingGraphFilter } from './vesting-graph-filter';

type VestingGraphCardProps = {
  companyId: string;
  enabled: boolean;
  isTasksError: boolean;
  stakeholderId: string;
  vestingTasks: VestingTask[];
};

const CardShell: FC<{ action?: ReactNode; children?: ReactNode; title: ReactNode }> = ({
  action,
  children,
  title,
}) => (
  <div className="flex w-full flex-col gap-4 rounded-md bg-white px-4 py-5 shadow-sm">
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0 flex-1">{title}</div>
      {action}
    </div>
    {children}
  </div>
);

export const VestingGraphCard: FC<VestingGraphCardProps> = ({
  companyId,
  enabled,
  isTasksError,
  stakeholderId,
  vestingTasks,
}) => {
  const [selectedVestingTaskId, setSelectedVestingTaskId] = useState('');

  const graphFilterOptions = vestingTasks
    .filter((vestingTask) => vestingTask.grantItem.plan.vesting)
    .map((vestingTask) => ({
      label: formatShortGrantId(vestingTask.grantItem.id),
      value: vestingTask.id,
    }));
  const selectedGraphVestingTaskId = graphFilterOptions.some(
    (option) => option.value === selectedVestingTaskId,
  )
    ? selectedVestingTaskId
    : (graphFilterOptions[0]?.value ?? '');

  const { isError, isFetching, isLoading, isPlaceholderData, vestingGraph } =
    VestingTaskHooks.useVestingGraph({
      companyId,
      enabled: enabled && Boolean(selectedGraphVestingTaskId),
      stakeholderId,
      vestingTaskId: selectedGraphVestingTaskId,
    });

  const headerAction =
    graphFilterOptions.length > 1 ? (
      <VestingGraphFilter
        onChange={setSelectedVestingTaskId}
        options={graphFilterOptions}
        value={selectedGraphVestingTaskId}
      />
    ) : null;

  if (isTasksError && !selectedGraphVestingTaskId) {
    return (
      <div className="flex flex-col gap-3">
        <CardShell
          title={
            <p className="text-label-sm font-medium text-gray-700">
              <AppFormattedMessage id="VESTING_GRAPH_ERROR" />
            </p>
          }
        />
      </div>
    );
  }

  if (!selectedGraphVestingTaskId) return null;

  // keepPreviousData would otherwise keep painting grant A after grant B's request fails.
  const isFailedSwitch = Boolean(isError && isPlaceholderData && !isFetching);
  const displayedGraph = isFailedSwitch ? undefined : vestingGraph;
  const isInitialLoading = isLoading && !displayedGraph;
  const isSwitchingGrant = Boolean(isPlaceholderData && isFetching);
  const panelKey = isInitialLoading
    ? 'vesting-graph-loading'
    : isError && !displayedGraph
      ? 'vesting-graph-error'
      : !displayedGraph?.points.length
        ? 'vesting-graph-empty'
        : (displayedGraph.vestingTaskId ?? selectedGraphVestingTaskId);

  const title = isInitialLoading ? (
    <div className="flex flex-col gap-2">
      <div className="h-5 w-2/3 rounded bg-gray-100" />
      <div className="h-4 w-1/2 rounded bg-gray-100" />
    </div>
  ) : displayedGraph ? (
    <div className="flex min-w-0 flex-col gap-0.5">
      <CrossfadeText
        className="block text-sm font-semibold text-gray-700"
        textKey={`vesting-graph-title-${displayedGraph.vestingTaskId}`}
      >
        <AppFormattedMessage
          id="VESTING_GRAPH_TITLE"
          values={{
            grantId: formatShortGrantId(displayedGraph.grantId),
            planName: displayedGraph.planName,
          }}
        />
      </CrossfadeText>
      <CrossfadeText
        className="block text-label-sm text-gray-500"
        textKey={`vesting-graph-subtitle-${displayedGraph.vestingTaskId}`}
      >
        <VestingGraphSubtitle parts={buildVestingGraphSubtitleParts(displayedGraph)} />
      </CrossfadeText>
    </div>
  ) : isError ? (
    <p className="text-label-sm font-medium text-gray-700">
      <AppFormattedMessage id="VESTING_GRAPH_ERROR" />
    </p>
  ) : (
    <p className="text-label-sm font-medium text-gray-500">
      <AppFormattedMessage id="VESTING_GRAPH_EMPTY" />
    </p>
  );

  return (
    <div className="flex flex-col gap-3">
      <CardShell action={headerAction} title={title}>
        <AnimatePresence initial={false} mode="wait">
          {isInitialLoading ? (
            <CrossfadePanel className="w-full" panelKey={panelKey} translate={false}>
              <div className="h-[240px] w-full animate-pulse rounded bg-gray-50" />
            </CrossfadePanel>
          ) : isError && !displayedGraph ? null : !displayedGraph?.points.length ? null : (
            <CrossfadePanel
              className={twMerge(
                'w-full transition-opacity duration-200 ease-out',
                isSwitchingGrant && 'opacity-55',
              )}
              panelKey={panelKey}
              translate={false}
            >
              <VestingGraph allowHover={!isSwitchingGrant} vestingGraph={displayedGraph} />
            </CrossfadePanel>
          )}
        </AnimatePresence>
      </CardShell>
    </div>
  );
};
